import { useState, useEffect } from 'react';
import { adminAPI, getQRCodeImageUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Download, QrCode, ExternalLink } from 'lucide-react';

export default function QRCodePage() {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getQRCode().then(res => setQrData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = getQRCodeImageUrl();
    link.download = 'qrcode-mietwagen-gb-sylt.png';
    link.click();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="qrcode-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">QR-Code</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Zentraler QR-Code für die Mietwagen-Seite</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-body font-semibold text-slate-700 flex items-center gap-2">
              <QrCode className="w-4 h-4 text-[#0E518D]" /> QR-Code Vorschau
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <div className="bg-white p-6 border border-slate-200 rounded-sm mb-4">
              <img
                src={getQRCodeImageUrl()}
                alt="QR Code"
                className="w-48 h-48"
                data-testid="qr-code-image"
              />
            </div>
            <Button onClick={handleDownload} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body" data-testid="qr-download-button">
              <Download className="w-4 h-4 mr-2" /> Als PNG herunterladen
            </Button>
            <p className="text-xs text-slate-400 font-body mt-2">Standard: 1000 x 1000 px PNG</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-body font-semibold text-slate-700">QR-Code Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-slate-500 font-body mb-1">Ziel-URL</p>
              <div className="flex items-center gap-2">
                <Input value={qrData?.qr_url || ''} readOnly className="rounded-sm font-body font-mono text-xs" data-testid="qr-url-display" />
                <a href={qrData?.qr_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-sm">
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="bg-slate-50 p-4 rounded-sm border border-slate-100">
              <h4 className="font-body font-semibold text-sm text-slate-800 mb-2">Ablauf</h4>
              <ol className="text-xs text-slate-600 font-body space-y-2 list-decimal list-inside">
                <li>Nutzer scannt den QR-Code</li>
                <li>Wird zu /qr weitergeleitet</li>
                <li>Zugriff wird mit source=qrcode getrackt</li>
                <li>Weiterleitung zur Startseite</li>
              </ol>
            </div>
            <div className="bg-amber-50 p-4 rounded-sm border border-amber-200">
              <p className="text-xs text-amber-800 font-body">
                Die Ziel-URL kann unter Einstellungen geändert werden.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
